import React, { useState, createContext, useCallback, ReactNode } from "react";
import { Pojo } from "types/Galaxy";
import { ProcessusDefinition } from "types/Processus";
import { getProcessusDefinition, getProcessusDefinitionColumn } from "api/processus";
import { getFKTableName } from "api/contextMenu";
import { track } from "tracking";

export interface ContextMenuState {
  show: boolean;
  definition: ProcessusDefinition[];
  xpos: number;
  ypos: number;
  sjmoCode: string | null;
  origineTableName: string;
  origineTableId: string;
  columnName: string | null;
  genericEntity: Pojo | null;
  forceNewTab: boolean;
  contextTableName: string | null;
  contextIds: string[] | null;
  entityFromView: boolean;
}

export interface ShowContextMenuParams {
  xpos: number;
  ypos: number;
  sjmoCode: string;
  contextTableName: string;
  columnName: string | null;
  contextId: string | null;
  value: string;
  genericEntity: Pojo | null;
  urlCplt?: string;
  forceNewTab?: boolean;
  entityFromView?: boolean;
}

export interface ContextMenuContextValue {
  state: ContextMenuState;
  showContextMenu: (params: ShowContextMenuParams) => Promise<void>;
  hideContextMenu: () => void;
}

const initialState: ContextMenuState = {
  show: false,
  definition: [],
  xpos: 0,
  ypos: 0,
  sjmoCode: null,
  columnName: null,
  origineTableName: "",
  origineTableId: "",
  genericEntity: null,
  forceNewTab: false,
  contextTableName: null,
  contextIds: null,
  entityFromView: false
};

const defaultContextValue: ContextMenuContextValue = {
  state: initialState,
  showContextMenu: async () => {},
  hideContextMenu: () => {}
};

export const ContextMenuContext = createContext<ContextMenuContextValue>(defaultContextValue);

export function ContextMenuProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ContextMenuState>(initialState);

  const showContextMenu = useCallback(async (params: ShowContextMenuParams) => {
    // 1. Track analytics
    track("contextmenu::open", {
      module: params.sjmoCode,
      table: params.contextTableName,
      column: params.columnName
    });

    try {
      let contextTableName = params.contextTableName;
      let contextId = params.contextId;

      // 2. Appel API selon columnName
      let definition: ProcessusDefinition[];

      if (params.columnName === null) {
        const response = await getProcessusDefinition(
          params.sjmoCode,
          params.contextTableName
        );
        definition = response.data;
      } else {
        const response = await getProcessusDefinitionColumn(
          params.sjmoCode,
          params.contextTableName,
          params.columnName
        );
        definition = response.data;

        // 3. Vérifier si c'est une FK
        const responseFK = await getFKTableName(
          params.sjmoCode,
          params.contextTableName,
          params.columnName
        );

        if (responseFK.data && responseFK.data !== null) {
          contextTableName = responseFK.data;
          contextId = params.value;
        }
      }

      setState(prev => ({
        ...prev,
        show: true,
        xpos: params.xpos,
        ypos: params.ypos,
        sjmoCode: params.sjmoCode,
        columnName: params.columnName,
        definition,
        origineTableName: params.contextTableName,
        origineTableId: params.contextId ?? "",
        genericEntity: params.genericEntity,
        forceNewTab: params.forceNewTab ?? false,
        contextTableName,
        contextIds: contextId ? [contextId] : null,
        entityFromView: params.entityFromView ?? false
      }));
    } catch (error) {
      console.error("Erreur lors du calcul du menu contextuel:", error);
    }
  }, []);

  const hideContextMenu = useCallback(() => {
    setState(initialState);
  }, []);

  const contextValue: ContextMenuContextValue = {
    state,
    showContextMenu,
    hideContextMenu
  };

  return <ContextMenuContext.Provider value={contextValue}>{children}</ContextMenuContext.Provider>;
}
